import { computed, DestroyRef, effect, inject, Signal, signal, untracked } from '@angular/core';
import { rxResource, takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, EMPTY, filter, map, Observable, switchMap } from 'rxjs';
import { LOCAL_STORAGE } from '../tokens/local-storage.token';
import { __ } from './object.functions';

/** Version marker written into every envelope so foreign / legacy values can be told apart. */
const ENVELOPE_VERSION = 1 as const;

/** Default debounce, in milliseconds, before a pending local change is pushed to the API. */
const DEFAULT_DEBOUNCE_MS = 500;

/** The shape the API returns on load and (as `updatedAt`) acknowledges on save. */
export type RemoteSetting<T> = {
  value: T;
  updatedAt: number;
};

/**
 * Optional API layer. `load` is required when `api` is present; `save` is optional so a
 * setting can be read-only from the server (local edits then stay authoritative locally).
 */
export type SignalSettingApi<T> = {
  load: () => Observable<RemoteSetting<T>>;
  save?: (value: T) => Observable<{ updatedAt: number }>;
  debounceTime?: number;
};

export type SignalSettingOptions<T> = {
  key: string;
  /** The fallback when nothing is stored / fetched. Pass a factory to derive it lazily (e.g. from OS preference). */
  defaultValue: T | (() => T);
  /** A function to validate the value before it is read. Drops invalid values. */
  validate?: (value: unknown) => value is T;
  api?: SignalSettingApi<T>;
};

export type SignalSetting<T> = {
  value: Signal<T>;
  isLoading: Signal<boolean>;
  error: Signal<unknown>;
  isPending: Signal<boolean>;
  set(value: T): void;
  update(updater: (current: T) => T): void;
  reset(): void;
  clear(): void;
  reload(): void;
};

/** What we actually persist to storage: the value plus the metadata the resolver needs. */
type StoredEnvelope<T> = {
  value: T;
  updatedAt: number;
  pending: boolean;
};

/**
 * Creates a single persisted signal backed by local storage, and — when an `api` is provided —
 * kept in sync with the server. The value resolves by recency: a pending local edit always wins,
 * otherwise the newer `updatedAt` wins (which, when not pending, means the server can reflect
 * changes made on another device). Server timestamps are the authority for that comparison, so
 * client clock skew can never resolve a conflict the wrong way.
 *
 * MUST be called inside an injection context (a field initializer or constructor) — it uses
 * `inject`, `effect`, and `rxResource`, all of which require one, exactly like the service that
 * owns it.
 *
 * @param options - Storage key, default value, optional value guard, and optional API layer.
 * @returns {SignalSetting<T>} A readonly value signal plus imperative set / update / reset / clear / reload.
 */
export function createSignalSetting<T>(options: SignalSettingOptions<T>): SignalSetting<T> {
  const { key, defaultValue, validate, api } = options;

  const resolveDefault = (): T =>
    __.IsFunction(defaultValue) ? (defaultValue as () => T)() : defaultValue;

  const storage: Storage | null = inject(LOCAL_STORAGE);
  const destroyRef = inject(DestroyRef);

  // -------------------------------------------------------------------------------------------------
  // Storage codec — JSON with a safe-parse fallback for legacy raw strings / numbers.
  // -------------------------------------------------------------------------------------------------

  const parseEnvelope = (raw: string): StoredEnvelope<T> => {
    try {
      const parsed: unknown = JSON.parse(raw);

      if (__.IsObject(parsed) && (parsed as { __ss?: number }).__ss === ENVELOPE_VERSION) {
        const envelope = parsed as StoredEnvelope<T> & { __ss: number };
        return {
          value: envelope.value,
          updatedAt: envelope.updatedAt ?? 0,
          pending: envelope.pending ?? false,
        };
      }

      // Valid JSON but not our envelope — a bare value written elsewhere (e.g. "collapsed", 42).
      return { value: parsed as T, updatedAt: 0, pending: false };
    } catch {
      // Not JSON at all — a legacy bare string such as an un-quoted `collapsed`.
      return { value: raw as unknown as T, updatedAt: 0, pending: false };
    }
  };

  const readLocal = (): StoredEnvelope<T> | null => {
    if (__.IsNullOrUndefined(storage)) {
      return null;
    }

    const raw = storage.getItem(key);
    if (__.IsNullOrUndefinedOrEmpty(raw)) {
      return null;
    }

    const envelope = parseEnvelope(raw);

    if (!__.IsNullOrUndefined(validate) && !validate(envelope.value)) {
      // Corrupt or from an older schema — drop it and fall back to default / server.
      return null;
    }

    return envelope;
  };

  const writeLocal = (envelope: StoredEnvelope<T>): void => {
    if (__.IsNullOrUndefined(storage)) {
      return;
    }

    storage.setItem(key, JSON.stringify({ __ss: ENVELOPE_VERSION, ...envelope }));
  };

  // -------------------------------------------------------------------------------------------------
  // State — local envelope is the writable source; the server resource is read-only.
  // -------------------------------------------------------------------------------------------------

  const localEnvelope = signal<StoredEnvelope<T> | null>(readLocal());

  const remote = !__.IsNullOrUndefined(api)
    ? rxResource<RemoteSetting<T>, unknown>({ stream: () => api.load() })
    : null;

  const remoteEnvelope = computed<RemoteSetting<T> | null>(() => remote?.value() ?? null);

  // -------------------------------------------------------------------------------------------------
  // Resolution — pending-local wins, else newer `updatedAt` wins, else default.
  // -------------------------------------------------------------------------------------------------

  const value = computed<T>(() => {
    const local = localEnvelope();
    const server = remoteEnvelope();

    if (!__.IsNullOrUndefined(local) && local.pending) {
      return local.value;
    }
    if (!__.IsNullOrUndefined(server) && !__.IsNullOrUndefined(local)) {
      return server.updatedAt >= local.updatedAt ? server.value : local.value;
    }
    if (!__.IsNullOrUndefined(server)) {
      return server.value;
    }
    if (!__.IsNullOrUndefined(local)) {
      return local.value;
    }

    return resolveDefault();
  });

  // -------------------------------------------------------------------------------------------------
  // Effects — persist locally, and cache a winning server value back into local storage.
  // -------------------------------------------------------------------------------------------------

  effect(() => {
    const envelope = localEnvelope();
    if (__.IsNullOrUndefined(envelope)) {
      return;
    }

    writeLocal(envelope);
  });

  effect(() => {
    const server = remoteEnvelope();
    if (__.IsNullOrUndefined(server)) {
      return;
    }

    const local = untracked(localEnvelope);

    // Keep local while it has un-pushed edits, or while it is at least as fresh as the server.
    if (!__.IsNullOrUndefined(local) && (local.pending || local.updatedAt >= server.updatedAt)) {
      return;
    }

    localEnvelope.set({ value: server.value, updatedAt: server.updatedAt, pending: false });
  });

  // -------------------------------------------------------------------------------------------------
  // Push pipeline — debounce pending local edits to the API, then clear pending on ack.
  // -------------------------------------------------------------------------------------------------

  if (!__.IsNullOrUndefined(api?.save)) {
    const save = api.save;

    toObservable(localEnvelope)
      .pipe(
        filter((env): env is StoredEnvelope<T> => !__.IsNullOrUndefined(env) && env.pending),
        debounceTime(api.debounceTime ?? DEFAULT_DEBOUNCE_MS),
        switchMap((env) =>
          save(env.value).pipe(
            map((ack) => ({ sent: env, updatedAt: ack.updatedAt })),
            // Keep the edit pending on failure — it retries on the next change / reload.
            catchError(() => EMPTY),
          ),
        ),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe(({ sent, updatedAt }) => {
        const current = untracked(localEnvelope);

        // Only clear pending if nothing newer was written while the save was in flight.
        if (!__.IsNullOrUndefined(current) && current.updatedAt === sent.updatedAt) {
          localEnvelope.set({ value: current.value, updatedAt, pending: false });
        }
      });
  }

  // -------------------------------------------------------------------------------------------------
  // Public surface.
  // -------------------------------------------------------------------------------------------------

  const setValue = (next: T): void => {
    localEnvelope.set({ value: next, updatedAt: Date.now(), pending: true });
  };

  return {
    value,
    isLoading: computed(() => remote?.isLoading() ?? false),
    error: computed(() => remote?.error() ?? null),
    isPending: computed(() => localEnvelope()?.pending ?? false),

    set: setValue,

    update(updater: (current: T) => T): void {
      setValue(updater(value()));
    },

    reset(): void {
      setValue(resolveDefault());
    },

    clear(): void {
      if (!__.IsNullOrUndefined(storage)) {
        storage.removeItem(key);
      }

      localEnvelope.set(null);
    },

    reload(): void {
      remote?.reload();
    },
  };
}
